import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import Hero from './components/Hero';
import FeaturedProducts from './components/FeaturedProducts';
import PlanetsCollection from './components/PlanetsCollection';
import About from './components/About';
import CustomerPromise from './components/CustomerPromise';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import PrivacyPolicy from './components/PrivacyPolicy';
import ShippingReturns from './components/ShippingReturns';
import Shop from './components/Shop';
import Cart from './components/Cart';
import Blog from './components/Blog';
import BlogPost from './components/BlogPost';
import EnvBadge from './components/EnvBadge';
import TermsOfService from './components/TermsOfService';
import CookiePolicy from './components/CookiePolicy';
import DataRequest from './components/DataRequest';
import Unsubscribe from './components/Unsubscribe';
import NotFound from './components/NotFound';
import CookieBanner from './components/CookieBanner';
import PageMeta from './components/PageMeta';
import AnalyticsListener from './components/AnalyticsListener';
import './styles/LightTheme.css';

// CART
import { CartProvider } from './context/CartContext';

// CUSTOMER ACCOUNTS
import { AuthProvider } from './context/AuthContext';
import RequireAuth from './components/account/RequireAuth';
import AccountLogin from './components/account/AccountLogin';
import AccountLayout from './components/account/AccountLayout';
import AccountDashboard from './components/account/AccountDashboard';
import AccountOrders from './components/account/AccountOrders';
import AccountConsultations from './components/account/AccountConsultations';
import AccountProfile from './components/account/AccountProfile';
import AccountPassword from './components/account/AccountPassword';

// ADMIN PAGES
import AdminLayout from './admin/AdminLayout';
import AdminRoute from './admin/AdminRoute';
import AdminLogin from './admin/auth/AdminLogin';
import Dashboard from './admin/pages/Dashboard';
import Products from './admin/pages/Products';
import Orders from './admin/pages/Orders';
import Coupons from './admin/pages/Coupons';
import Vendor from './admin/pages/Vendor';
import Users from './admin/pages/Users';
import Consultations from './admin/pages/Consultations';
import Reviews from './admin/pages/Reviews';
import Newsletter from './admin/pages/Newsletter';
import AdminBlog from './admin/pages/Blog';
import Account from './admin/pages/Account';
import './admin/styles/admin.css';

// Global mobile hardening — imported last so it overrides component CSS
import './styles/Responsive.css';

/* Public site chrome: starfield + header + footer around every public page.
   Admin routes render outside this layout so they get a clean slate. */
function PublicLayout() {
  const [loading, setLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence mode="wait">
      {loading ? (
        <LoadingScreen key="loading" />
      ) : (
        <motion.div
          key="content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Header scrolled={scrolled} />
          <Outlet />
          <Footer />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* Scroll to top on route change — unless we arrived from a nav link
   asking for a specific section on the home page. */
function ScrollToTop() {
  const { pathname, state } = useLocation();

  useEffect(() => {
    const target = state?.scrollTo;

    if (target) {
      // Wait for the destination route to render before scrolling.
      const timer = setTimeout(() => {
        document
          .querySelector(target)
          ?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
      return () => clearTimeout(timer);
    }

    window.scrollTo(0, 0);
  }, [pathname, state]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          {/* Per-route <title> and description; index.html only ever
              had one set for the whole site. */}
          <PageMeta />
          {/* Sends a Google Analytics page view on every route change —
              a single-page app fires gtag's own one only once. */}
          <AnalyticsListener />
          {/* Shows "STAGING" on *.vercel.app, nothing on the live
              domain. Safe to merge; delete when no longer wanted. */}
          <EnvBadge />
          <div className="App">
            <Routes>
              {/* PUBLIC SITE */}
              <Route element={<PublicLayout />}>
                <Route
                  path="/"
                  element={
                    <>
                      <Hero />
                      {/* Products come before the planet chart: the page
                          has to read as a shop, not a consultancy. */}
                      <FeaturedProducts />
                      <PlanetsCollection />
                      <About />
                      {/* Last thing before the footer. It answers the
                          "can I trust this shop" question at the point
                          someone has finished reading and is deciding. */}
                      <CustomerPromise />
                    </>
                  }
                />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route
                  path="/planets"
                  element={
                    <>
                      <PlanetsCollection />
                      <CustomerPromise />
                    </>
                  }
                />
                <Route path="/shipping-returns" element={<ShippingReturns />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route path="/data-request" element={<DataRequest />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />

                {/* CUSTOMER ACCOUNT */}
                <Route path="/account/login" element={<AccountLogin />} />
                <Route
                  path="/account"
                  element={
                    <RequireAuth>
                      <AccountLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<AccountDashboard />} />
                  <Route path="orders" element={<AccountOrders />} />
                  <Route
                    path="consultations"
                    element={<AccountConsultations />}
                  />
                  <Route path="profile" element={<AccountProfile />} />
                  <Route path="password" element={<AccountPassword />} />
                </Route>

                {/* Anything else: a real 404 inside the site chrome,
                    rather than the blank middle an unmatched path used
                    to render. */}
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* ADMIN LOGIN (public, no site chrome) */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* PROTECTED ADMIN PANEL */}
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="products" element={<Products />} />
                <Route path="orders" element={<Orders />} />
                <Route path="vendor" element={<Vendor />} />
                <Route path="coupons" element={<Coupons />} />
                <Route path="users" element={<Users />} />
                <Route path="consultations" element={<Consultations />} />
                <Route path="reviews" element={<Reviews />} />
                <Route path="newsletter" element={<Newsletter />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="account" element={<Account />} />
              </Route>
            </Routes>
            {/* Asks once, and nothing reaches Google until it's
                answered. */}
            <CookieBanner />
          </div>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
